import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import StorageIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';

interface DocumentSettingsProps {
  settings: any;
  onUpdate: (settings: any) => Promise<void>;
}

export default function DocumentSettings({ settings, onUpdate }: DocumentSettingsProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    max_file_size_mb: settings.max_file_size_mb || 50,
    max_files_per_upload: settings.max_files_per_upload || 10,
    max_total_documents: settings.max_total_documents || 10000,
    allowed_file_types: settings.allowed_file_types || ['pdf', 'docx', 'txt', 'md'],
    auto_delete_processed_files: settings.auto_delete_processed_files ?? true,
    retention_days: settings.retention_days || 365,
    enable_ocr: settings.enable_ocr ?? true,
    ocr_languages: settings.ocr_languages || ['de', 'en'],
    max_processing_time_minutes: settings.max_processing_time_minutes || 30
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleFileTypesChange = (value: string) => {
    const types = value.split(',').map(type => type.trim()).filter(type => type);
    handleChange('allowed_file_types', types);
  };

  const handleLanguagesChange = (value: string) => {
    const langs = value.split(',').map(lang => lang.trim()).filter(lang => lang);
    handleChange('ocr_languages', langs);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(formData);
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('system.documents.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('system.documents.description')}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('system.documents.uploadLimits')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.maxFileSize')}
              type="number"
              value={formData.max_file_size_mb}
              onChange={(e) => handleChange('max_file_size_mb', parseInt(e.target.value) || 0)}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">MB</InputAdornment>,
                },
              }}
              helperText={t('system.documents.maxFileSizeHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.maxFilesPerUpload')}
              type="number"
              value={formData.max_files_per_upload}
              onChange={(e) => handleChange('max_files_per_upload', parseInt(e.target.value) || 0)}
              helperText={t('system.documents.maxFilesPerUploadHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.maxTotalDocuments')}
              type="number"
              value={formData.max_total_documents}
              onChange={(e) => handleChange('max_total_documents', parseInt(e.target.value) || 0)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <StorageIcon />
                    </InputAdornment>
                  ),
                },
              }}
              helperText={t('system.documents.maxTotalDocumentsHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.allowedFileTypes')}
              value={formData.allowed_file_types.join(', ')}
              onChange={(e) => handleFileTypesChange(e.target.value)}
              placeholder="pdf, docx, txt, md"
              helperText={t('system.documents.allowedFileTypesHelp')}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.documents.retention')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.auto_delete_processed_files}
                  onChange={(e) => handleChange('auto_delete_processed_files', e.target.checked)}
                />
              }
              label={t('system.documents.autoDeleteFiles')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.retentionDays')}
              type="number"
              value={formData.retention_days}
              onChange={(e) => handleChange('retention_days', parseInt(e.target.value) || 0)}
              disabled={!formData.auto_delete_processed_files}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">{t('common.days')}</InputAdornment>,
                },
              }}
              helperText={t('system.documents.retentionDaysHelp')}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.documents.processing')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enable_ocr}
                  onChange={(e) => handleChange('enable_ocr', e.target.checked)}
                />
              }
              label={t('system.documents.enableOcr')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.ocrLanguages')}
              value={formData.ocr_languages.join(', ')}
              onChange={(e) => handleLanguagesChange(e.target.value)}
              disabled={!formData.enable_ocr}
              placeholder="de, en, fr, es"
              helperText={t('system.documents.ocrLanguagesHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.documents.maxProcessingTime')}
              type="number"
              value={formData.max_processing_time_minutes}
              onChange={(e) => handleChange('max_processing_time_minutes', parseInt(e.target.value) || 0)}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">{t('common.minutes')}</InputAdornment>,
                },
              }}
              helperText={t('system.documents.maxProcessingTimeHelp')}
            />
          </Grid>

          <Grid size={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('system.documents.storageInfo', {
                maxSize: formData.max_file_size_mb,
                maxFiles: formData.max_total_documents
              })}
            </Alert>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {t('common.save')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}